import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Box, Button, Flex, Grid, Group, List, LoadingOverlay, Modal, Text, Title } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { IconCheck, IconUpload } from "@tabler/icons-react";
import { File as _File } from "src/graphql/generated/graphql";
import { useUploadImage } from "src/hooks/files/useUploadImage";
import { handleError } from "src/utils/error";

import { useFileInput } from "../../hooks/fileInput";
import { createModal } from "../../hooks/modals";
import { readImageDimensions } from "../../utils/readImageDimensions";
import { CropImageModal, CropImageModalProps } from "../CropImage";
import { SelectableImage } from "./selectableImage";

export interface SelectImageModalProps {
    title?: string;
    files?: _File[];
    openFileInputDialogOnMount?: boolean;
    crop?: Omit<CropImageModalProps, "imageSrc" | "onSave">;
    onSelect?(image: _File);
}

export const SelectImageModal = createModal<SelectImageModalProps>(
    ({ title, files, openFileInputDialogOnMount, crop, onSelect, onClose }) => {
        const { t } = useTranslation(["common"]);

        const [
            { type: selectedFileType, id: selectedFileId } = {
                type: null,
                id: null,
            },
            setSelectedFile,
        ] = useState<{
            type: "uploaded" | "existing";
            id: string;
        }>();

        const { uploadImage, isUploadingImage } = useUploadImage();
        const [uploadedImage, setUploadedImage] = useState<_File>();

        const handleUploadFile = useCallback(
            async (file: File) => {
                const image = await uploadImage(file);
                setUploadedImage(image);
                setSelectedFile({
                    type: "uploaded",
                    id: image?.id,
                });
            },
            [uploadImage]
        );

        const handleCropSelectedFile = useCallback(
            async (file: File) => {
                const reader = new FileReader();

                const handleLoad = async () => {
                    const imageSrc = reader.result?.toString?.();

                    if (!imageSrc) {
                        alert("Error. Do smth different.");
                        return;
                    }

                    if (crop?.minWidth || crop?.minHeight) {
                        const [width, height] = await readImageDimensions(imageSrc);

                        if (crop?.minWidth && width < crop.minWidth) {
                            showNotification({
                                color: "red",
                                title: t("image_widthtoosmall"),
                                message: t("image_widthtoosmall_message", {
                                    minPx: crop.minWidth,
                                    originalPx: width,
                                }),
                                autoClose: 1000 * 10,
                            });
                            return;
                        }

                        if (crop?.minHeight && height < crop.minHeight) {
                            showNotification({
                                color: "red",
                                title: t("image_heighttoosmall"),
                                message: t("image_heighttoosmall_message", {
                                    minPx: crop.minHeight,
                                    originalPx: height,
                                }),
                                autoClose: 1000 * 10,
                            });
                            return;
                        }
                    }

                    if (!crop?.aspectRatio?.value) {
                        handleUploadFile(file);
                        return;
                    }

                    CropImageModal.display({
                        ...crop,
                        imageSrc,
                        onSave: handleUploadFile,
                    });
                };

                reader.addEventListener("load", () => {
                    handleLoad().catch((err) => {
                        handleError(err, { context: { imageSrc: reader.result?.toString?.() } });
                    });
                });

                reader.readAsDataURL(file);
            },
            [crop, handleUploadFile, t]
        );

        const handleSelectedFile = useCallback(
            async (file: File) => {
                if (crop) {
                    handleCropSelectedFile(file);
                    return;
                }

                handleUploadFile(file);
            },
            [crop, handleCropSelectedFile, handleUploadFile]
        );

        const { fileInputElement, openFileInputDialog } = useFileInput({
            onSelect: handleSelectedFile,
        });

        useEffect(() => {
            if (openFileInputDialogOnMount) {
                setTimeout(openFileInputDialog, 24);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        const handleConfirmSelection = useCallback(() => {
            if (!selectedFileType || !selectedFileId) {
                return;
            }

            if (selectedFileType === "uploaded") {
                onSelect?.(uploadedImage);
                onClose?.();
                return;
            }

            const file = (files || []).find((file) => file?.id === selectedFileId);
            onSelect?.(file);
            onClose?.();
        }, [files, onClose, onSelect, selectedFileId, selectedFileType, uploadedImage]);

        return (
            <Modal size="lg" title={<Title order={3}>{title || t("image_upload")}</Title>} onClose={onClose} opened>
                {fileInputElement}
                <LoadingOverlay visible={isUploadingImage} />
                <Text size="lg" weight={500}>
                    {t("uploadedimage")}
                </Text>
                {crop && (
                    <Alert mt="xs" color="orange" title={t("image_requirements")}>
                        <List>
                            {crop?.aspectRatio?.value && (
                                <List.Item>
                                    {t("image_requirement_aspectratio")}{" "}
                                    <Text component="span" weight={500}>
                                        {crop.aspectRatio.label}
                                    </Text>
                                </List.Item>
                            )}
                            {crop?.minWidth && (
                                <List.Item>
                                    {t("image_requirement_minwidth")}{" "}
                                    <Text component="span" weight={500}>
                                        {crop.minWidth}px
                                    </Text>
                                </List.Item>
                            )}
                            {crop?.minHeight && (
                                <List.Item>
                                    {t("image_requirement_minheight")}{" "}
                                    <Text component="span" weight={500}>
                                        {crop.minHeight}px
                                    </Text>
                                </List.Item>
                            )}
                        </List>
                    </Alert>
                )}
                <Flex mt="xs" wrap="wrap-reverse" align="start">
                    {uploadedImage?.id && (
                        <Box style={{ flex: 1 }}>
                            <SelectableImage
                                file={uploadedImage}
                                isSelected={selectedFileId === uploadedImage?.id}
                                containerProps={{ maw: "200px" }}
                                onSelect={() =>
                                    setSelectedFile({
                                        type: "uploaded",
                                        id: uploadedImage?.id,
                                    })
                                }
                            />
                        </Box>
                    )}
                    <Button leftIcon={<IconUpload size="1em" />} onClick={openFileInputDialog}>
                        {t("image_upload")}
                    </Button>
                </Flex>
                {!!files?.length && (
                    <>
                        <Text mt="lg" size="lg" weight={500}>
                            {t("existingimageselection")}
                        </Text>
                        <Grid mt="xs">
                            {files.map((file, index) => (
                                <Grid.Col key={`${index}${file?.id}${file?.objectKey}`} xs={12} sm={6} md={4}>
                                    <SelectableImage
                                        file={file}
                                        isSelected={selectedFileId === file?.id}
                                        onSelect={() =>
                                            setSelectedFile({
                                                type: "existing",
                                                id: file?.id,
                                            })
                                        }
                                    />
                                </Grid.Col>
                            ))}
                        </Grid>
                    </>
                )}
                <Group mt="lg" position="right">
                    <Button variant="subtle" onClick={onClose}>
                        {t("cancel")}
                    </Button>
                    <Button
                        leftIcon={<IconCheck size="1em" />}
                        disabled={!selectedFileId}
                        onClick={handleConfirmSelection}
                    >
                        {t("confirmselectedimage")}
                    </Button>
                </Group>
            </Modal>
        );
    }
);
